import { GetServerSidePropsContext } from "next";
import { ParsedUrlQuery } from "querystring";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import getChannelConfigByPathName from "src/api/channel";
import { checkUnknownError } from "src/utils/errors";
import { LOGICAL_GROUP_CHANNEL_CODE } from "src/constants/logicalGroupChannelCode";
import PlanContentDirectLinkPage from "src/components/PlanContentDirectLinkPage";
import {
  getProductCategoryActiveListViaServerSide,
  getProductCategoryByCode,
} from "src/api/productCategory";
import ProductTypes from "src/types/productType";
import { Banner as BannerType } from "src/types/banner";
import { getPlanByCode, getPlanStarterPrice } from "src/api/plan";
import { withLayout } from "src/components/Layout/withLayout";
import withUserOrGuest from "src/components/Auth/withUserOrGuest";

export interface CustomParams extends ParsedUrlQuery {
  channelPath: string;
  categoryCode: string;
  planCode: string;
}

export async function getServerSideProps(
  context: GetServerSidePropsContext<CustomParams>
) {
  const categoryCode = context.params?.categoryCode;
  const channelPath = context.params?.channelPath;
  const planCode = context.params?.planCode;

  // check categoryCode or planCode must not empty
  if (!categoryCode || !channelPath || !planCode) {
    return {
      notFound: true,
    };
  }

  try {
    const channelConfig = await getChannelConfigByPathName(channelPath);

    if (!channelConfig) {
      return {
        notFound: true,
      };
    }

    if (channelConfig.logicalGroupChannelCode !== LOGICAL_GROUP_CHANNEL_CODE) {
      return {
        notFound: true,
      };
    }

    const channelCode = channelConfig.code;
    const systemChannelCode = channelConfig.code;

    const planContentDirectLinkAPIInfo = await Promise.all([
      getProductCategoryActiveListViaServerSide(channelCode),
      getProductCategoryByCode(categoryCode, channelCode),
      getPlanByCode(planCode, categoryCode, systemChannelCode),
      getPlanStarterPrice(channelCode, categoryCode, planCode),
    ]);

    const productCategoryActiveList = planContentDirectLinkAPIInfo[0];
    const productCategory = planContentDirectLinkAPIInfo[1];
    const plan = planContentDirectLinkAPIInfo[2];
    const starterPrice = planContentDirectLinkAPIInfo[3];

    if (
      !productCategoryActiveList ||
      !productCategory ||
      !productCategory.active ||
      !plan ||
      !starterPrice
    ) {
      return {
        redirect: {
          destination: `/${channelPath}`,
        },
      };
    }

    // redirect to externalLink or to go plan page
    if (productCategory.type === ProductTypes.external) {
      return {
        redirect: {
          destination: productCategory.externalLink,
          permanent: false,
        },
      };
    }

    const banner: BannerType = {
      title: {
        en: productCategory.displayName.en,
        th: productCategory.displayName.th,
      },
      desktopImage: productCategory.banner?.desktopUrl || null,
      mobileImage: productCategory.banner?.mobileUrl || null,
    };

    return {
      props: {
        channelPath,
        channelConfig,
        plan,
        starterPrice,
        productCategory,
        banner,
        productCategoryActiveList,
        ...(await serverSideTranslations(context.locale, [
          "plan-page",
          "plan-content-direct-link",
          "entry-form",
          "common",
          "cookie-consent",
          "main-page",
          "plan",
          "layout",
          "otp-form",
          "form",
          "error-dialog",
        ])),
      },
    };
  } catch (error) {
    const isUnknownError = checkUnknownError(error);

    if (!isUnknownError) {
      return {
        redirect: {
          destination: `/${channelPath}`,
        },
      };
    }

    throw error;
  }
}

export default withUserOrGuest(withLayout(PlanContentDirectLinkPage));
